// Reads grow attribution from the landing page URL and persists it for the
// duration of the visit so every CTA on the page can pass it through to
// Shopify checkout. Mirrors the params written by the /r/{slug} redirect:
//   - brella_click_id  — opaque token the orders/create webhook joins back
//     to GrowLinkClick to attribute the order
//   - utm_source / utm_medium / utm_campaign / utm_content / utm_term
//   - discount         — overlap with the Shopify /discount/{code} stage,
//                        kept so we can render an "applied" callout
//   - ref              — creator-readable shorthand a few merchants use
//
// Storage is sessionStorage (per-tab, cleared when the tab closes) so the
// page can be visited many times without stale attribution leaking across
// sessions, while still surviving in-page client routing.

import { CHAT_API_BASE } from '../config/api';

const STORAGE_KEY = 'brella_landing_attribution';

const ATTRIBUTION_KEYS = [
  'brella_click_id',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'discount', 'ref',
];

function readUrlParams() {
  if (typeof window === 'undefined') return {};
  const sp = new URLSearchParams(window.location.search || '');
  const out = {};
  ATTRIBUTION_KEYS.forEach((k) => {
    const v = sp.get(k);
    if (v && v.trim()) out[k] = v.trim();
  });
  return out;
}

function readStored() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) {
    return {};
  }
}

function writeStored(obj) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (_) { /* private mode — non-fatal */ }
}

/**
 * Merge any attribution params in the current URL into sessionStorage,
 * favoring fresh URL values over stored ones. Returns the combined map.
 * Safe to call on every render; cheap and idempotent.
 */
export function captureAttribution() {
  const fresh = readUrlParams();
  if (Object.keys(fresh).length === 0) return readStored();
  const next = { ...readStored(), ...fresh };
  writeStored(next);
  return next;
}

/** Returns the stored attribution (does not consult the URL). */
export function getAttribution() {
  return readStored();
}

/**
 * Build a [{name, value}] array suitable for Shopify `attributes[]` on the
 * /cart/add.js call. Keeps non-utm keys ('discount', 'ref') out of cart
 * attributes since those belong on the checkout URL instead.
 */
export function attributionAsCartAttributes() {
  const a = readStored();
  const entries = [];
  if (a.brella_click_id) entries.push({ name: 'brella_click_id', value: a.brella_click_id });
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
    if (a[k]) entries.push({ name: k, value: a[k] });
  });
  return entries;
}

export function getDiscountCode() {
  const a = readStored();
  return (a.discount || '').trim() || null;
}

/**
 * Line-item property tying LP-originated cart lines back to the page. The
 * orders/create webhook (protect's LandingPageAttributionService) reads it
 * to bump the page's stats.conversions — this is what makes conversions
 * count for DIRECT visits, where there is no brella_click_id.
 */
export function lpLineProperty(page) {
  return page?.id ? { _brella_lp_id: page.id } : {};
}

/**
 * Fire-and-forget CTA-click ping (stats.ctaClicks). sendBeacon survives the
 * imminent navigation to /cart; keepalive fetch is the fallback. Counted on
 * cart-intent clicks only (add-to-cart / bundle / buy-now), not scroll CTAs.
 */
export function pingLandingPageCta(page) {
  if (typeof window === 'undefined' || !page?.shopId || !page?.slug) return;
  const url = `${CHAT_API_BASE}/landing-pages/public/${encodeURIComponent(page.shopId)}/${encodeURIComponent(page.slug)}/cta`;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
  } catch (_) { /* fall through to fetch */ }
  try {
    fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  } catch (_) { /* best-effort */ }
}
